import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { PublicQuoteSelection, PublicQuoteView } from '@bandido/shared/types';
import { downloadPublicQuote } from './publicQuoteTransport';
export function PublicQuotePdf({
  token,
  view,
  selection,
  disabled,
  onBusy,
}: {
  token: string;
  view: PublicQuoteView;
  selection: PublicQuoteSelection;
  disabled: boolean;
  onBusy: (busy: boolean) => void;
}) {
  const { t, i18n } = useTranslation(),
    tr = (key: string) => t(`commercialQuotes.pdf.${key}`);
  const [busy, setBusy] = useState(false),
    [failed, setFailed] = useState(false);
  const pending = useRef(false),
    active = useRef(true),
    reportBusy = useRef(onBusy),
    controller = useRef<AbortController>();
  reportBusy.current = onBusy;
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
      controller.current?.abort();
      if (pending.current) {
        pending.current = false;
        reportBusy.current(false);
      }
    };
  }, []);
  async function download() {
    if (pending.current || disabled) return;
    pending.current = true;
    setBusy(true);
    setFailed(false);
    onBusy(true);
    const abort = new AbortController();
    controller.current = abort;
    const timer = setTimeout(() => abort.abort(), 45000);
    try {
      const blob = await downloadPublicQuote(
        token,
        view.versionId,
        selection,
        i18n.language.startsWith('en') ? 'en' : 'es',
        abort.signal,
      );
      if (!active.current) return;
      const url = URL.createObjectURL(blob),
        anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `proposal-v${view.version}.pdf`;
      anchor.rel = 'noreferrer';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      if (active.current) setFailed(true);
    } finally {
      clearTimeout(timer);
      pending.current = false;
      if (active.current) {
        setBusy(false);
        onBusy(false);
      }
    }
  }
  return (
    <Stack spacing={1}>
      {failed && <Alert severity="warning">{tr('failed')}</Alert>}
      <Button disabled={disabled || busy} onClick={() => void download()}>
        {tr(busy ? 'preparing' : 'download')}
      </Button>
    </Stack>
  );
}
